import { FC, useRef, KeyboardEvent, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { CountryData, defaultCountries, PhoneInput } from 'react-international-phone';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { uaPhonePattern, usPhonePattern } from '@/constants/validation';
import { selectPhoneNumber, setPhoneNumber } from '@/store/reducers/survey/surveySlice';
import { selectIsLoading, selectIsUserFromMobile } from '@/store/reducers/user/userSlice';
import { requestOTPCode } from '@/store/reducers/user/actions';
import AuthForm from '../forms/AuthForm';
import useSurveyNavigate from '@/hooks/useSurveyNavigate';
import analytics from '@/utils/analytics/analyticsInstance';

type FormDataType = {
  phone: string;
};

const customMasks: Record<string, string> = {
  us: '... ... ....',
  ua: '.. ... .. ..',
};

const PhoneNumberStep: FC = () => {
  const dispatch = useAppDispatch();
  const inputRef = useRef<HTMLInputElement>(null);
  const phoneNumber = useAppSelector(selectPhoneNumber);
  const isFromMobile = useAppSelector(selectIsUserFromMobile);
  const isLoading = useAppSelector(selectIsLoading);
  const { next } = useSurveyNavigate();
  const {
    control,
    formState: { isValid },
    handleSubmit,
  } = useForm<FormDataType>({
    mode: 'onSubmit',
    defaultValues: { phone: phoneNumber },
  });

  const countries = defaultCountries
    .map(([name, iso2, dialCode]) => {
      if (!(iso2 in customMasks)) return null;

      return [name, iso2, dialCode, customMasks[iso2]] as CountryData;
    })
    .filter((c): c is CountryData => c !== null);

  const onSubmit = async ({ phone }: FormDataType) => {
    const place = isFromMobile ? 'mobile' : 'web';

    await analytics.track('authSendCodeClicked', { place });
    await dispatch(requestOTPCode({ phoneNumber: phone, context: { place }, callback: next }));
    dispatch(setPhoneNumber(phone));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>, value: string) => {
    const input = inputRef.current;

    if (e.key === 'Backspace' && input?.selectionStart === 1 && value.startsWith('+')) {
      e.preventDefault();
    }
  };

  const handleFocus = useCallback(() => {
    analytics.track('authPhoneNumberInputSelected', {
      place: isFromMobile ? 'mobile' : 'web',
    });
  }, [isFromMobile]);

  useEffect(() => {
    analytics.track('webOnboardingScreenOpened', {
      screen_title: 'Phone number',
    });
    analytics.track('authStarted', {
      place: isFromMobile ? 'mobile' : 'web',
    });
  }, [isFromMobile]);

  return (
    <AuthForm<FormDataType>
      title="Enter your phone number"
      input={{
        control,
        name: 'phone',
        rules: {
          required: {
            value: true,
            message: 'This field is required',
          },
          validate: (value) => {
            if (usPhonePattern.test(value)) return true;
            if (uaPhonePattern.test(value)) return true;
            return 'Phone number is not valid';
          },
        },
        render: ({ field: { value, onChange } }) => (
          <PhoneInput
            inputRef={inputRef}
            autoFocus
            value={value}
            countries={countries}
            disableFormatting={false}
            onFocus={handleFocus}
            onChange={onChange}
            style={{ outline: 'none' }}
            inputProps={{
              type: 'tel',
              onKeyDown: (e) => handleKeyDown(e, value),
            }}
          />
        ),
      }}
      buttons={[
        {
          id: 'submit',
          name: 'Continue',
          loading: isLoading,
          disabled: !isValid || isLoading,
          onClick: handleSubmit(onSubmit),
        },
      ]}
    />
  );
};

export default PhoneNumberStep;
